import type { Permission } from '@today/types'
import type { ComponentType } from 'react'

/** Props a plugin admin page receives. */
export interface AdminPluginSectionProps {
	tenantSlug: string
	code: string
}

/**
 * A plugin-registered admin section (TO-189 REQ-005). It belongs to one tenant:
 * only that tenant's viewers holding `permission` see it, at `/admin/plugins/{code}`.
 */
export interface AdminPluginSection {
	code: string
	label: string
	tenantSlug: string
	permission: Permission
	component: ComponentType<AdminPluginSectionProps>
}

/** A plugin admin section registration is malformed; thrown when the registry is built. */
export class AdminPluginSectionRegistryError extends Error {
	constructor(message: string) {
		super(message)
		this.name = 'AdminPluginSectionRegistryError'
	}
}

const CODE_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/
const MAX_CODE_LENGTH = 40

/**
 * Validates plugin admin sections and returns them in registration order.
 * Codes appear in URLs, so they must be unique kebab-case of 1–40 characters.
 *
 * @throws AdminPluginSectionRegistryError on a malformed or duplicate code, or a blank label.
 */
export function buildAdminPluginRegistry(sections: readonly AdminPluginSection[]): readonly AdminPluginSection[] {
	const seen = new Set<string>()
	for (const { code, label } of sections) {
		if (code.length > MAX_CODE_LENGTH || !CODE_PATTERN.test(code)) {
			throw new AdminPluginSectionRegistryError(`Invalid plugin admin section code "${code}"`)
		}
		if (seen.has(code)) throw new AdminPluginSectionRegistryError(`Duplicate plugin admin section code "${code}"`)
		if (label.trim() === '') throw new AdminPluginSectionRegistryError(`Plugin admin section "${code}" has no label`)
		seen.add(code)
	}
	return sections
}

/** The plugin admin sections shipped in this build. None yet: Epic 16 plugins register here. */
export const ADMIN_PLUGIN_SECTIONS: readonly AdminPluginSection[] = buildAdminPluginRegistry([])
